// @flow
import * as React from 'react';
import {createContext, useContext, useEffect, useRef, useState} from 'react';
import type {Store} from 'nanostores';

export type AnyStore<T> = Store<T>;

type CtxType<TStores> = TStores | null;

/**
 * Factory: context + hooks
 * - useStore: return store instance without subscription. (nanostore object)
 * - useActions: bind actions once
 * - useStoreSelector: subscribe to a slice
 * - useStores: return all stores instances with subscription
 * - StoresProvider: provider component
 */
export function createStoresContext<
  TStores: {[string]: AnyStore<mixed>, ...},
  TInitial
>(createStores: (initial?: TInitial) => TStores) {
  const Ctx: React.Context<CtxType<TStores>> = createContext<CtxType<TStores>>(
    null
  );

  function StoresProvider({
    children,
    initial,
  }: {
    children: React$Node,
    initial?: TInitial,
  }) {
    const ref = useRef<TStores>(null);
    if (ref.current == null) {
      ref.current = createStores(initial);
    }
    // $FlowExpectedError[incompatible-type] - guaranteed non-null by previous branch
    return <Ctx.Provider value={ref.current}>{children}</Ctx.Provider>;
  }

  function useStores(): TStores {
    const ctx = useContext<CtxType<TStores>>(Ctx);
    if (ctx == null) {
      throw new Error('useStores must be used within <StoresProvider>.');
    }
    return ctx;
  }

  function useStore<T>(pick: (s: TStores) => AnyStore<T>): AnyStore<T> {
    const stores = useStores();
    return pick(stores);
  }

  function useActions<T>(bind: (s: TStores) => T): T {
    const stores = useStores();
    const actionsRef = useRef<T>(null);
    if (actionsRef.current == null) {
      actionsRef.current = bind(stores);
    }
    // $FlowExpectedError[incompatible-return]
    return actionsRef.current;
  }

  function useStoreSelector<S, T>(
    pick: (s: TStores) => AnyStore<S>,
    select?: (state: S) => T,
    isEqual?: (a: T, b: T) => boolean
  ): T {
    const store = pick(useStores());
    const eq: (a: T, b: T) => boolean = isEqual || Object.is;

    const [value, setValue] = useState<T>(() =>
      select ? select(store.get()) : store.get()
    );

    useEffect(() => {
      const unsubscribe = store.subscribe((next: S) => {
        const nextValue = select ? select(next) : next;
        setValue(prev => (eq(prev, nextValue) ? prev : nextValue));
      });
      return unsubscribe;
    }, [eq, select, store]);

    return value;
  }

  return {StoresProvider, useStores, useStore, useActions, useStoreSelector};
}
